// Copyright: IQGeo Limited 2010-2019

import 'babel-polyfill';
import Promise from 'bluebird';
import MywClass from 'myWorld/base/class';
import EventsMixin from 'myWorld/base/eventsMixin';

const myw = {};
const versionTag = document.head.querySelector('[name=myworld-version]');
const buildVersionTag = document.head.querySelector('[name=myworld-project-build-version]');
myw.version = versionTag && versionTag.content;
myw.buildVersion = buildVersionTag && buildVersionTag.content;

/* globals global:false */

//provide myw, and Bluebird as globals
global.myw = myw;
global.Promise = Promise;

//detects if chrome dev tools are open.
const isDevToolsOpen = (() => {
    //if dev tools are open, printing an image will call the id property
    let open = false;
    try {
        const element = new Image();
        Object.defineProperty(element, 'id', {
            get: function() {
                open = true;
                return undefined;
            }
        });
        console.log('%cHello', element);
    } catch (e) {
        //ignore errors, will return false;
    }
    return open;
})();
Promise.config({
    warnings: {
        // Enables all warnings except forgotten return statements.
        wForgottenReturn: false
    },
    longStackTraces: isDevToolsOpen
});

/**
 * Provides classical inheritance model. To be inherited by myWorld classes<br/>
 * Provides localisation methods
 * @name MywClass
 * @constructor
 * @extends LocalisationMixin
 */
myw.MywClass = myw.Class = MywClass;

myw.Events = EventsMixin;

/**
 * Object where feature model classes can be registered. <br/> Keyed on feature type name.
 * When a feature type is registered here, feature objects obtained from the database will inherit from the specified model.
 * @type {Object<string,class>}
 */
myw.featureModels = {};

/**
 * Object where datasource classes can be registered. <br/> Keyed on datasource type.
 * @type {Object}
 */
myw.datasourceTypes = {};

/**
 * Whether the current environment is the native app or not
 * @type {boolean}
 */
myw.isNativeApp = false;

/**
 * Object where modules can register a new tab in the Settings page of Configuration
 */
myw.configPagesSettingsTabs = {};

/**
 * Outputs line of text to browser console.
 * @param  {string}     module The module this trace line applies to.
 * @param  {integer}    level If this number is higher than or equal to trace level for the module then the line is written out.
 * @param  {...string}     msgs Message to show in console.
 */
myw.trace = (module, level, ...msgs) => {
    // ENH: If calculating msg in the caller proves to be costly then one option is to allow caller to pass a function/closure that
    // will only be evaluated if the trace level is appropriate.
    if (myw.isTracing(module, level)) {
        msgs.unshift(`${module}:`);

        const isChrome = navigator.userAgent.indexOf('Chrome') !== -1;
        if (isChrome) {
            const error = new Error();
            Error.captureStackTrace(error, myw.trace);
            const stack = error.stack;
            const file = stack
                .split('\n')[1]
                .split('/')
                .splice(-1)[0]
                .split(':')[0]
                .split('?')[0];
            const line = stack
                .split('\n')[1]
                .split('/')
                .splice(-1)[0]
                .split(':')[1];
            msgs.push('\t\t\t');
            msgs.push(`(${file}:${line})`);
        }

        if (level > 0) {
            const now = new Date(),
                msec = now.getMilliseconds(),
                pad = ['00', '0', ''][Math.floor(Math.log(msec) / Math.LN10)];

            console.log(
                `[${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}:${pad}${msec}] ${msgs.join(
                    ' '
                )}`
            );
        } else {
            console.log(...msgs);
        }
    }
};

//trace to use in promise handlers. Ex: promise.tap(myw.tracef(module, level, ...msgs))
myw.tracef = function() {
    const args = Array.prototype.slice.call(arguments);
    args.unshift(myw);
    return myw.trace.bind(...args);
};

/**
 * Returns whether tracing is active for given module & level.
 * Useful for map related debugging.
 * @param  {string}     module The module this trace line applies to.
 * @param  {integer}    level If this number is higher than or equal to trace level for the module then the line is written out.
 * @return {boolean}
 */
myw.isTracing = (module, level) =>
    (module in myw.tracing.modules && myw.tracing.modules[module] >= level) ||
    ('all' in myw.tracing.modules && myw.tracing.modules['all'] >= level);

myw.tracing = {};

let traceSettings;
try {
    const traceSettingsStr = localStorage.getItem('mywTrace');
    traceSettings = traceSettingsStr && JSON.parse(traceSettingsStr);
} catch (e) {
    //ignore errors getting from local storage
}
myw.tracing.modules = traceSettings || {};

/**
 * Set the trace level for the specified module. Apart from the 'all' module, there is no explicitly defined list of modules. Specifying the 'all'
 * module allows a minimum trace level to be set across all modules. Suggested use during development
 * is to use the browser debugger console to make a call to myw.traceset to set the desired trace level.
 * @param {string}  module Module name
 * @param {integer} level  Required trace level
 */
myw.traceset = (module, level) => {
    myw.tracing.modules[module] = level;
    try {
        localStorage.setItem('mywTrace', JSON.stringify(myw.tracing.modules));
    } catch (e) {
        //ignore as this isn't crucial
    }
};

export default myw;

/**
 * A Promise complying with the specification in ECMAScript 6.
 * See {@link https://github.com/petkaantonov/bluebird/blob/master/API.md} for reference
 * @typedef Promise
 */

/**
 * @typedef {Promise} promise
 */
